<script>
import {defineComponent, reactive} from "vue";
import 'quill/dist/quill.snow.css';
import ResourceDisplay from '../ResourceDisplay.vue';
import {useLandingPageStore} from "../../../stores/landingPage";


export default defineComponent({
  name: "PageArticle",
  components: {
    ResourceDisplay
  },
  props: {
    /**
     * The layout of the article.
     * imageRight & image left
     */
    layout: {
      type: String,
      default: 'imageLeft',
      validator: (value) => {
        return ['imageLeft', 'imageRight'].includes(value);
      }
    },
    // props used in all layouts
    /**
     * Header text
     */
    title: {
      type: String,
      default: 'New Content Section Title'
    },
    /**
     * Main body text
     * Either a string or an array of strings.
     * Each string will be its own paragraph.
     */
    bodyText: {
      type: [String, Array],
      default: "Add description text here"
    },
    /**
     * Resource Id of the article image
     * Uses resource array in landing store to populate
     */
    articleImage: {
      type: Number,
      required: false
    },
    /**
     * The alt attribute for the image.
     */
    imageAlt: {
      type: String,
      default: ''
    },
  },
  setup(props) {
    props = reactive(props);
    const landingStore = useLandingPageStore();
    return {
      landingStore
    };
  },
  data() {
    return {};
  },
  computed: {
    sanitizedBodyText() {
      if (!this.bodyText) return '';
      // Prefix href values that do not start with
      // http://, https://, mailto:, tel:, #, or / with https://
      return this.bodyText.replace(
        /href="(?!https?:\/\/|\/|#|mailto:|tel:)([^"]+)"/gi,
        'href="https://$1"'
      );
    },
  },
  methods: {},
});

</script>

<template>
  <div
    v-if="articleImage || bodyText || title"
    class="outer-container"
  >
    <div
      class="inner-container"
    >
      <div
        id="article-container"
      >
        <div :class="['article-content-container', `layout--${layout}`]">
          <div class="text-container">
            <div
              v-if="articleImage"
              class="image-container"
            >
              <ResourceDisplay
                :resource-id="articleImage"
                :alt="imageAlt"
              />
            </div>
            <h1 class="title-text">
              {{ title }}
            </h1>
            <div
              v-if="bodyText"
              class="ql-snow"
            >
              <div
                class="ql-editor article-body-text-container body-1-regular"
                v-html="sanitizedBodyText"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.inner-container {
  margin-top: $space-xs !important;
}

#article-container {
  background-color: white;
  border-radius: $radius-rounded;
  box-shadow: $shadow-card;
  padding: $space-md;

  .article-content-container {
    height: 100%;
    width: 100%;
    overflow: hidden;

    .text-container {
      .article-body-text-container {
        p:first-child {
          margin-top: $space-sm;
        }

        p:not(:first-child) {
          margin-top: $space-lg;
        }
      }
    }

    .image-container {
      overflow: hidden;
      width: 33%;
      min-height: 200px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    :deep(img) {
      @apply w-full h-full;
      object-fit: cover;
      border-radius: $radius-rounded;
      opacity: 0;
      transition: opacity 0.4s ease;
    }

    &.layout--imageLeft {
      .image-container {
        float: left;
        margin-right: $space-2xl;
        margin-bottom: $space-lg;
      }
    }

    &.layout--imageRight {
      .image-container {
        float: right;
        margin-left: $space-2xl;
        margin-bottom: $space-lg;
      }
    }

    // Tablet — keep float but adjust image size
    @media screen and (min-width: $breakpoint-min-sm) and (max-width: $breakpoint-max-lg) {
      .image-container {
        width: 40%;
        min-height: 150px;
      }
    }

    // Mobile — stack vertically
    @media screen and (max-width: $breakpoint-max-sm) {
      .image-container {
        float: none !important;
        width: 100% !important;
        margin: 0 0 $space-lg 0 !important;
      }
    }

    // Container responsiveness - only for preview mode on builder!

    // Tablet preview
    @container (min-width: 480px) and (max-width: 1023px) {
      .image-container {
        width: 40% !important;
        min-height: 150px;
      }
    }

    // Mobile preview — stack vertically
    @container (min-width: 0px) and (max-width: 479px) {
      .image-container {
        float: none !important;
        width: 100% !important;
        margin: 0 0 $space-lg 0 !important;
      }
    }

    .title-text {
      font-size: $section-title-size;
      font-weight: $section-title-weight;
      line-height: $section-title-line;
      color: $color-neutral-900;
    }

    p {
      word-break: break-word;
    }

    .ql-editor {
      padding: 0;
    }

    .ql-snow,
    .ql-snow .ql-editor {
      display: contents;
    }

    :deep(.ql-editor blockquote) {
      background: transparent !important;
      border-left: none !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    :deep(.ql-editor blockquote p) {
      display: inline !important;
      margin: 0 !important;
    }

    :deep(.ql-editor blockquote::before) {
      content: '“';
      font-size: 1.2em;
      vertical-align: text-bottom;
      margin-right: 0.25em;
    }

    :deep(.ql-editor blockquote::after) {
      content: '”';
      font-size: 1.2em;
      vertical-align: text-bottom;
      margin-left: 0.25em;
    }

    :deep(.ql-editor *) {
      background-color: transparent !important;
      background: transparent !important;
      font-family: inherit !important;
    }
  }
}
</style>
